import { PlusOutlined } from '@ant-design/icons';
import { FormErrorsAlert } from '@framework/form/form/errors/FormErrorsAlert.tsx';
import { FormHandler } from '@framework/form/form/FormHandler.tsx';
import { type RelationEditor } from '@framework/form/form/relations/useRelationEditor.ts';
import { Drawer } from '@framework/ui/components/drawer/Drawer.tsx';
import { DrawerButtonRow } from '@framework/ui/components/drawer/DrawerButtonRow.tsx';
import { Button, message, Space } from 'antd';
import { assignUniqueLayerCode } from '../layers/assignUniqueLayerCode.ts';
import { type IMapGroup } from '../layers/MapGroup.ts';
import { type IMapLayer, MapLayerType } from '../layers/MapLayer.ts';
import { MapLayerSchema } from '../layers/schema/MapLayerSchema.ts';
import { MapLayerConfigInput } from './MapLayerConfigInput.tsx';

export interface IMapLayerConfigDrawerButtonProps {
  editor: RelationEditor<IMapLayer>;
  allowedTypes?: MapLayerType[];
  groups?: IMapGroup[];
}

export function MapLayerConfigDrawerButton(
  props: IMapLayerConfigDrawerButtonProps,
) {
  const { editor, allowedTypes, groups } = props;
  return (
    <div>
      <Button
        type="dashed"
        block
        size="large"
        icon={<PlusOutlined aria-hidden="true" />}
        onClick={e => {
          editor.handle.open(
            MapLayerSchema.cast({ type: MapLayerType.OSM }, { assert: false }),
          );
          e.stopPropagation();
        }}
      >
        Aggiungi Livello
      </Button>
      {/* Modal Drawer  */}
      <Drawer
        title={editor.handle.isNew ? 'Crea Livello' : 'Modifica Livello'}
        size="large"
        placement="right"
        destroyOnClose
        onClose={editor.handle.cancel}
        open={editor.handle.isVisible}
      >
        <FormHandler
          nested
          layout="vertical"
          schema={MapLayerSchema}
          disableEnterSubmit
          initialValues={
            editor.handle.current ??
            MapLayerSchema.cast({ type: MapLayerType.OSM }, { assert: false })
          }
          onSubmit={async (values, e) => {
            try {
              const current = editor.handle.current;
              // Il code è calcolato dal nome: qui lo rendiamo univoco rispetto
              // ai layer fratelli (escluso quello in modifica).
              const usedCodes = new Set(
                editor.items
                  .filter(layer => layer !== current)
                  .map(layer => layer.code)
                  .filter(Boolean),
              );
              const layerToApply = assignUniqueLayerCode(values, usedCodes);
              const type = editor.handle.apply(layerToApply);
              if (type === 'updated') {
                message.success('Livello modificato');
              } else {
                message.success('Livello aggiunto');
              }
            } catch (e) {
              message.error(
                'Si è verificato un errore nella modifica del livello',
              );
              console.error(e);
            }
          }}
        >
          <FormErrorsAlert />
          <MapLayerConfigInput
            prefix={''}
            allowedTypes={allowedTypes}
            groups={groups}
          />

          <DrawerButtonRow>
            <Space>
              <Button size="large" onClick={editor.handle.cancel}>
                Annulla
              </Button>
              <Button type="primary" htmlType="submit" size="large">
                Applica
              </Button>
            </Space>
          </DrawerButtonRow>
        </FormHandler>
      </Drawer>
    </div>
  );
}
